/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  IconButton,
  CircularProgress,
  Button,
  LinearProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close } from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { useSnackbar } from 'notistack';

import { ROLES } from '../../constants/general';
import { MODALITY_OPTIONS_LABELS } from '../../constants/observations';
import FacilityService from '../../services/facility.service';
import ModalityMultiSelect from '../common/ModalityMultiSelect';
import QuizCollectionService from '../../services/quizCollection.service';
import UserService from '../../services/user.service';
import SearchInput from '../common/SearchInput';

const useStyles = makeStyles((theme) => ({
  '& .MuiSvgIcon-root': {
    color: 'red !important',
  },
  '.MuiTable-root': {
    borderSpacing: 0,
  },
  '.MuiTableBody-root .MuiTableCell-root': {
    paddingBottom: '0 !important',
    paddingTop: '0 !important',
  },
  buttonsContainer: {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-end',
  },
  checkboxCell: {
    width: 50,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  closeIcon: {
    '&:hover': {
      color: '#333',
      cursor: 'pointer',
    },
    color: '#aaa',
    display: 'none !important',
    padding: 2,
    position: 'absolute',
    right: 20,
    top: 4,
  },
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '8px',
      maxWidth: 960,
    },
  },
  dialogContent: {
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
  },
  inputContainer: {
    '& .NativeDatepicker, .NativeDatepicker .MuiBox-root, & select': {
      height: 30,
      width: 140,
    },
    '&:hover>.MuiSvgIcon-fontSizeSmall': {
      display: 'block !important',
    },
    position: 'relative',
  },
  searchInputRoot: {
    '& .MuiInput-input': {
      height: 30,
      padding: '0 5px',
    },
    '& .MuiInput-root': {
      height: 30,
    },
    height: 30,
  },
  select: {
    '& .MuiInputBase-input': {
      color: '#999',
      fontFamily: 'Roboto',
      fontSize: 13,
      height: 30,
      padding: '0 5px',
    },
    '&:focus': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      outline: 'none',
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      cursor: 'pointer',
    },
    border: '1px solid #C8C8C8',
    borderRadius: 8,
    color: '#999',
    height: 30,
    overflow: 'hidden',
    width: 127,
  },
  tableBody: {
    borderSpacing: 0,
  },
  tableCell: {
    paddingBottom: '0 !important',
    paddingTop: '0 !important',
  },
  tableContainer: {
    height: '100%',
  },
  tableHeader: {
    borderBottom: '1px solid #EBEBEB',
  },
  tableHeaderCell: {
    backgroundColor: '#FFF',
    borderBottom: '1px solid #EBEBEB',
    fontSize: 12,
    fontWeight: 'bold',
  },
}));

const userService = new UserService();
const facilityService = new FacilityService();
const quizCollectionService = new QuizCollectionService();

const SendToTechsModal = ({ open, onClose, quizCollectionId }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFacility, setSelectedFacility] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedModalities, setSelectedModalities] = useState([]);
  const [selectedTechs, setSelectedTechs] = useState([]);
  const [technologists, setTechnologists] = useState([]);
  const [filteredTechs, setFilteredTechs] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const ROWS_PER_PAGE = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  useEffect(() => {
    if (open) {
      setPage(0);
      fetchFacilities();
      fetchTechnologists();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchTechnologists();
    }
  }, [open, page, searchTerm, selectedFacility, selectedDepartment, selectedModalities]);

  const fetchTechnologists = async () => {
    try {
      setIsLoading(true);
      const params = {
        limit: ROWS_PER_PAGE,
        page,
        roles: [ROLES.TECHNOLOGIST.role],
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (selectedFacility) {
        params.facilityId = selectedFacility;
      }

      if (selectedDepartment) {
        params.departmentId = selectedDepartment;
      }

      if (selectedModalities.length > 0) {
        params.modality = selectedModalities;
      }

      const response = await userService.getUsers(params);

      setTechnologists(response.data || []);
      setFilteredTechs(response.data || []);
      setTotalCount(response.total || 0);
    } catch (error) {
      console.error('Error fetching technologists:', error);
      enqueueSnackbar('Failed to load technologists', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFacilities = async () => {
    try {
      setLoadingFacilities(true);

      const data = await facilityService.getAllFacilities();

      setFacilities(data || []);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      enqueueSnackbar('Failed to load facilities', { variant: 'error' });
    } finally {
      setLoadingFacilities(false);
    }
  };

  const fetchDepartments = async (facilityId) => {
    try {
      setLoadingDepartments(true);

      const data = await facilityService.getDepartments(facilityId);

      setDepartments(data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      enqueueSnackbar('Failed to load departments', { variant: 'error' });
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
    setPage(0);
  };

  const handleFacilityChange = (event) => {
    const facilityId = event.target.value;
    setSelectedFacility(facilityId);
    setSelectedDepartment('');
    setDepartments([]);
    setPage(0);

    if (facilityId) {
      fetchDepartments(facilityId);
    }
  };

  const resetFacilityFilter = () => {
    setSelectedFacility('');
    setSelectedDepartment('');
    setDepartments([]);
    setPage(0);
  };

  const resetDepartmentFilter = () => {
    setSelectedDepartment('');
    setPage(0);
  };

  const handleModalityChange = (modalities) => {
    setSelectedModalities(modalities);
    setPage(0);
  };

  const handleSearchChange = (searchValue) => {
    setSearchTerm(searchValue);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage - 1);
  };

  const handleSelectTech = (id) => {
    setSelectedTechs((prev) => {
      if (prev.includes(id)) {
        return prev.filter((techId) => techId !== id);
      }
      return [...prev, id];
    });
  };

  const handleSend = async () => {
    if (selectedTechs.length === 0) {
      enqueueSnackbar('Please select at least one technologist', { variant: 'warning' });
      return;
    }

    const selectedTechData = technologists.filter((tech) => selectedTechs.includes(tech.id));

    try {
      if (quizCollectionId) {
        const technologistIds = selectedTechData.map((tech) => tech.id);
        const response = await quizCollectionService.sendToTechnologists(quizCollectionId, {
          technologistIds,
        });

        enqueueSnackbar(response.message || `Quiz sent to ${selectedTechData.length} technologist(s)`, { variant: 'success' });
      }

      setSelectedTechs([]);
      setSearchTerm('');
      setSelectedFacility('');
      setSelectedDepartment('');
      setSelectedModalities([]);
      setDepartments([]);
      setPage(0);
      onClose();
    } catch (error) {
      console.error('Error sending quiz to technologists:', error);
      enqueueSnackbar('Failed to send quiz to technologists', { variant: 'error' });
    }
  };

  return (
    <Dialog
      className={classes.dialog}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '8px',
          height: 'min(calc(100vh - 200px),800px)',
          maxWidth: 700,
          padding: 30,
        },
      }}>
      <DialogTitle>
        Send to techs
        <IconButton className={classes.closeButton} onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box width="100%" display="flex" flexDirection="column" gridGap={14} marginBottom={3}>
          <Box width="100%" display="flex" flexWrap="wrap" gridGap={10} alignItems="center">
            <SearchInput
              onSearch={() => {}}
              onKeywordUpdate={handleSearchChange}
              placeholder="Search"
              disableSearch
              searchValue={searchTerm}
              style={{ width: '100%' }}
              classes={{
                root: classes.searchInputRoot,
              }}
            />
          </Box>

          <Box alignItems="center" width="100%" display="flex" flexWrap="wrap" gridGap={10}>
            <div className={classes.inputContainer}>
              <select className={classes.select} id="facilitySelect" value={selectedFacility} onChange={handleFacilityChange}>
                <option value="">Facilities</option>
                {facilities.map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </select>

              {loadingFacilities && <LinearProgress style={{ height: 2, width: '100%' }} />}
              {!!selectedFacility && <Close fontSize="small" className={classes.closeIcon} title="Reset Value" onClick={resetFacilityFilter} />}
            </div>

            {selectedFacility && (
              <div className={classes.inputContainer}>
                <select className={classes.select} id="departmentSelect" value={selectedDepartment} onChange={handleDepartmentChange}>
                  <option value="">Departments</option>
                  {departments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>

                {loadingDepartments && <LinearProgress style={{ height: 2, width: '100%' }} />}
                {!!selectedDepartment && <Close fontSize="small" className={classes.closeIcon} title="Reset Value" onClick={resetDepartmentFilter} />}
              </div>
            )}

            <ModalityMultiSelect selectedModality={selectedModalities} onChange={handleModalityChange} placeholder="Modalities" multiple />
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table} stickyHeader>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell className={`${classes.checkboxCell} ${classes.tableHeaderCell}`} />
                  <TableCell className={classes.tableHeaderCell}>Name</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Email</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Modality</TableCell>
                </TableRow>
              </TableHead>
              <TableBody className={classes.tableBody}>
                {filteredTechs.map((tech) => (
                  <TableRow key={tech.id} hover>
                    <TableCell className={classes.tableCell}>
                      <Checkbox
                        style={{ color: selectedTechs.includes(tech.id) ? '#2AAAE2' : '#D6D6D6' }}
                        checked={selectedTechs.includes(tech.id)}
                        onChange={() => handleSelectTech(tech.id)}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>{tech.name || 'N/A'}</TableCell>
                    <TableCell className={classes.tableCell}>{tech.email || 'N/A'}</TableCell>
                    <TableCell className={classes.tableCell}>{MODALITY_OPTIONS_LABELS[tech.modality]}</TableCell>
                  </TableRow>
                ))}
                {filteredTechs.length === 0 && technologists.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No technologists found matching your search
                    </TableCell>
                  </TableRow>
                )}
                {technologists.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No technologists available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && totalCount > ROWS_PER_PAGE && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2} mb={2}>
            <Pagination count={Math.ceil(totalCount / ROWS_PER_PAGE)} page={page + 1} onChange={handlePageChange} color="primary" showFirstButton showLastButton />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Box className={classes.buttonsContainer} width="100%">
          <Button style={{ width: '200px' }} onClick={handleSend} color="primary" variant="contained" disabled={selectedTechs.length === 0 || isLoading}>
            Send
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SendToTechsModal;
